/**
 * `/o/:org/improvements` - the proposal queue.
 *
 * V1 section 9 asks for a filterable queue showing the base revision, a summary,
 * and Accept into draft / Request changes / Dismiss with reason.
 *
 * "Accept into draft" is the part that is not available in this build, and the
 * difference is worth stating rather than hiding. Accepting a proposal creates
 * the draft that carries the proposed change (`proposals.linked_draft_id`), and
 * that creation goes through the publication and revision machinery which is a
 * P2 function. The two decisions that are real are taken here: dismissing with
 * a reason, and accepting as a resolution that records who decided and when
 * without inventing a draft. The third control is present and disabled with the
 * reason attached, so the screen shows what the specification asks for and is
 * honest about which part this deployment cannot perform.
 */

import type { AppShell, ScreenState } from '../app.js';
import * as api from '../api/queries.js';
import { type Result, ok } from '../api/client.js';
import { type RawHtml, html } from '../lib/html.js';
import {
  PROPOSAL_CATEGORY_LABEL,
  PROPOSAL_STATUS_LABEL,
  formatDateTime,
  personLabel,
} from '../lib/format.js';
import { button, docHead, emptyState, failurePanel, tag } from '../ui/components.js';

type ProposalRowView = api.ProposalRow & { readonly skillTitle: string | null };

export interface ImprovementsData {
  readonly rows: readonly ProposalRowView[];
  readonly status: string;
}

export async function loadImprovements(
  app: AppShell,
  orgId: string,
): Promise<Result<ImprovementsData>> {
  const status = new URLSearchParams(app.state.search).get('status') ?? 'open';
  const rows = await api.listProposals(app.client, orgId, status);
  if (!rows.ok) return rows;
  return ok({ rows: rows.value, status });
}

export function renderImprovements(app: AppShell, screen: ScreenState): RawHtml {
  if (screen.kind === 'loading' || screen.kind === 'idle') {
    return html`
      ${docHead([{ label: 'Section', value: 'Improvements' }], 'Improvements')}
      <div class="panel"><p class="cell-note">Loading proposals.</p></div>
    `;
  }
  if (screen.kind === 'failed') {
    return html`
      ${docHead([{ label: 'Section', value: 'Improvements' }], 'Improvements')}
      ${failurePanel(screen.failure, { retry: 'improvements:reload' })}
    `;
  }

  const data = screen.data as ImprovementsData;
  return html`
    ${docHead(
      [
        { label: 'Section', value: 'Improvements' },
        { label: 'Filter', value: PROPOSAL_STATUS_LABEL[data.status] ?? data.status },
      ],
      'Improvements',
      'Proposals against published skills. Each one keeps the release it was written against, so a reviewer sees a stale base rather than a silently updated one.',
    )}

    <div class="toolbar">
      <label class="field-fieldset">
        <span class="field-label">Show</span>
        <select class="field" id="improvements-status" data-field="improvements.status">
          ${filterOption('open', 'Open', data.status)}
          ${filterOption('accepted', 'Accepted', data.status)}
          ${filterOption('dismissed', 'Dismissed', data.status)}
          ${filterOption('all', 'All', data.status)}
        </select>
      </label>
      <span class="topbar-spacer"></span>
      ${data.rows.length === 0 ? '' : button('Propose an improvement', 'improvements:propose', 'primary')}
    </div>

    ${data.rows.length === 0
      ? html`<div class="panel">
          ${emptyState({
            title: 'Nothing in this queue',
            body:
              data.status === 'open'
                ? 'No open proposals. Anyone with use permission on a skill can propose a correction, a failure, drift or a gap against a specific release.'
                : 'No proposals match this filter.',
            primary: null,
            secondary:
              data.status === 'open'
                ? []
                : [{ label: 'Show open', action: 'improvements:show-open' }],
          })}
        </div>`
      : data.rows.map((row) => proposalRecord(app, row))}
  `;
}

function filterOption(value: string, label: string, selected: string): RawHtml {
  return html`<option value="${value}"${value === selected ? ' selected' : ''}>${label}</option>`;
}

function proposalRecord(app: AppShell, row: ProposalRowView): RawHtml {
  const open = row.status === 'open';
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>${row.skillTitle ?? 'A skill you cannot see'}</h2>
        ${tag(PROPOSAL_STATUS_LABEL[row.status] ?? row.status, open ? 'O' : 'R', open ? 'attention' : 'plain')}
      </div>
      <div class="panel-body stack-sm">
        <dl class="dl">
          <dt>Category</dt>
          <dd>${PROPOSAL_CATEGORY_LABEL[row.category] ?? row.category}</dd>
          <dt>Reported by</dt>
          <dd>${personLabel(row.reporter_id, app.state.directory)}</dd>
          <dt>Written against</dt>
          <dd>${row.base_release_id === null ? 'no published release' : row.base_release_id.slice(0, 8)}</dd>
          <dt>Raised</dt>
          <dd>${formatDateTime(row.created_at)}</dd>
        </dl>
        <p>${row.summary}</p>
        ${row.resolution_note === null
          ? ''
          : html`<p class="cell-note">
              Resolved by ${personLabel(row.resolved_by, app.state.directory)} on ${formatDateTime(row.resolved_at ?? row.updated_at)}:
              ${row.resolution_note}
            </p>`}
        ${open
          ? html`
              <label class="field-label" for="proposal-note-${row.id}">Reason for your decision</label>
              <textarea
                class="field"
                id="proposal-note-${row.id}"
                rows="3"
                data-field="improvements.note.${row.id}"
                placeholder="Why this is accepted, or why it is dismissed"
              ></textarea>
              <div class="btn-row">
                ${button(
                  'Accept into draft',
                  `improvements:accept:${row.id}`,
                  'primary',
                  { disabled: 'disabled', title: 'Creating the revision draft is not available in this build.' },
                )}
                ${button(`Request changes`, `improvements:request:${row.id}`, 'secondary')}
                ${button(`Dismiss`, `improvements:dismiss:${row.id}`, 'danger')}
              </div>
              <p class="cell-note">
                Accepting records the decision and closes the queue item. It does not create the
                revision draft, because that creation is not available in this build.
              </p>
            `
          : ''}
        <span class="visually-hidden">${app.orgSlug}</span>
      </div>
    </div>
  `;
}